import { useTranslation } from "react-i18next";
import type { ProjectedHumanStep } from "../../../api/threads/liveMessages";
import Chip from "../../../ui/feedback/Chip";
import SpinnerIcon from "../../../ui/feedback/SpinnerIcon";
import { hostTurnText, requestWaitText } from "./requestWaitWords";
import { RUN_STATE_LABELS } from "./runStateLabels";
import type { ThreadDisplayStatus } from "./threadStatus";

interface ThreadStatusBadgeProps {
  status: ThreadDisplayStatus;
  /** The person step that waits, from the same projection: Input then says whom it waits for. */
  waiting?: ProjectedHumanStep | null;
  /** The host's name, `null` when the viewer hosts: where the turn is the host's, the badge names them. */
  hostName?: string | null;
  /** The viewer has opened the Run since it last changed (T3 Code's `hasUnseenCompletion`); unknown reads as seen. */
  seen?: boolean;
  compact?: boolean;
  className?: string;
}

/**
 * One status per Thread, the same in the list and on the page: module C's status, shown as the one table of Run state
 * labels says (`RUN_STATE_LABELS`); nothing where it shows no label. The hover keeps module C's exact explanation.
 */
export default function ThreadStatusBadge({ status, waiting = null, hostName = null, seen = true, compact = false, className }: ThreadStatusBadgeProps) {
  const { t } = useTranslation();
  const label = RUN_STATE_LABELS[status];
  const shown = label === null || (label.unseenOnly === true && seen) ? null : label;
  const turn = hostTurnText(status, waiting, hostName, t);
  if (shown === null && turn === null) return null;
  return (
    <Chip
      tone={shown?.tone ?? "info"}
      size={compact ? "compact" : "small"}
      title={status === "input" && waiting !== null ? requestWaitText(waiting, null, hostName, t) : t(`threads.status.${status}.explanation`)}
      icon={shown?.spinning === true ? <SpinnerIcon className="h-3 w-3" /> : null}
      className={className === undefined ? "shrink-0" : `shrink-0 ${className}`}
    >
      {turn ?? (shown === null ? null : t(`threads.status.${shown.word}.label`))}
    </Chip>
  );
}
